import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import FAQSection from '@/components/ui/FAQSection';
import { Camera, Building, Home, User, Utensils, Camera as CameraIcon, Video } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import ServiceCard from '@/components/ui/ServiceCard';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import PricingCard from '@/components/ui/PricingCard';

const Photography = () => {
  const photographyServices = [{
    icon: <User />,
    title: "Fashion Photography",
    description: "Professional fashion photography for brands, products, and models. We handle everything from model direction to location scouting for e-commerce or social media content.",
    badge: "Fashion"
  }, {
    icon: <Building />,
    title: "Real Estate Photography",
    description: "Standard MLS photography and premium architectural photography for real estate listings, capturing the essence and appeal of properties.",
    badge: "Real Estate"
  }, {
    icon: <Utensils />,
    title: "Food Photography",
    description: "Mouth-watering food photography for restaurants, food trucks, and food products that showcases your culinary creations in the best light.",
    badge: "Food"
  }, {
    icon: <Building />,
    title: "Retail Photography",
    description: "High-quality images of storefronts and interiors that showcase your retail space and products in a way that attracts customers.",
    badge: "Retail"
  }, {
    icon: <User />,
    title: "Lifestyle & Brand Photography",
    description: "Authentic lifestyle images for individuals, teams, and brands that tell your story and connect with your audience.",
    badge: "Lifestyle"
  }, {
    icon: <CameraIcon />,
    title: "Drone Photography",
    description: "Aerial photography for properties, events, and landscapes that provide a unique perspective and stunning visuals.",
    badge: "Drone"
  }];
  const faqs = [{
    question: "How long does a typical photography session take?",
    answer: "Session length varies by type. Fashion and product shoots typically take 3-5 hours, real estate sessions 1-3 hours depending on property size, and food photography 2-4 hours. We'll provide a specific timeline based on your project needs during our initial consultation."
  }, {
    question: "How quickly will I receive my photos after a shoot?",
    answer: "For most projects, you'll receive a digital gallery with your edited photos within 3-5 business days after the shoot. For larger projects or during peak seasons, delivery may take up to 7 business days. Rush delivery is available for an additional fee."
  }, {
    question: "Do you provide the raw, unedited photos?",
    answer: "We don't typically provide raw, unedited photos as part of our standard packages. Our professional editing process is an essential part of delivering the high-quality images that represent our brand standard. However, we're happy to discuss specific editing preferences during our consultation."
  }, {
    question: "What is your cancellation policy?",
    answer: "We understand that plans change. For cancellations made more than 48 hours before the scheduled shoot, we offer a full refund of your deposit. Cancellations within 48 hours of the shoot may result in forfeiture of the deposit, which can be applied to a rescheduled session within 60 days."
  }, {
    question: "Do you travel for photography sessions?",
    answer: "Yes! While we're based in Baton Rouge, Louisiana, we're available for travel throughout the United States. Travel fees may apply depending on the location. Evan has photographed in Europe, New York City, and various cities across the United States."
  }];
  const businessPricingCards = [{
    title: "Real Estate Photography",
    price: "",
    description: "Professional photography for real estate listings and architectural showcase.",
    ctaText: "View Pricing",
    ctaLink: "/services/real-estate-media"
  }, {
    title: "Professional Headshots",
    price: "$300",
    description: "30-minute session with 4 final edited photos. Multiple poses and outfit changes available.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Restaurant Photography",
    price: "Custom",
    description: "Showcase your culinary creations and restaurant atmosphere with professional food photography.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Fashion Photography",
    price: "Custom",
    description: "Professional fashion photography for brands, products, and models with location scouting.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Event Photography",
    price: "$550",
    description: "Starting at 2 hours of coverage for corporate events, conferences, and gatherings.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Content Creation",
    price: "Custom",
    description: "Professional photography for social media marketing, websites, and promotional materials.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }];
  const personalPricingCards = [{
    title: "Portrait Session",
    price: "$275",
    description: "One hour session with professional editing and online gallery delivery.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Engagement Portraits",
    price: "$275",
    description: "One hour session capturing your special moments with professional editing.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }, {
    title: "Graduation Portraits",
    price: "$275",
    description: "One hour session to commemorate your achievement with professional editing.",
    ctaText: "Email for Info",
    ctaLink: "mailto:hello@astrem.co?subject=Photography Inquiry"
  }];

  return (
    <PageLayout 
      title="Professional Photography | Astrem" 
      description="High-quality photography services for businesses, real estate, events, and portraits from Astrem in Baton Rouge, Louisiana."
      ogImage="public/lovable-uploads/cfc973c6-b5d3-4421-bdef-53921b6e763a.png"
    >
      <PageHero title="Professional Photography Services" subtitle="Capturing your vision with precision and artistry" description="Our photography services help businesses and individuals showcase their products, properties, and personality through high-quality, professionally captured images." badge="Visual Media in Baton Rouge, Louisiana" backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_taking_a_photograph_outside.jpg" ctaText="Get Started" ctaLink="mailto:hello@astrem.co?subject=Photography Inquiry" />
      
      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Photography Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              From real estate to product photography, we provide professional photography services tailored to your specific needs in Baton Rouge and beyond.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {photographyServices.map((service, index) => <ScrollReveal key={index} delay={200 + index * 50}>
                <ServiceCard icon={service.icon} title={service.title} description={service.description} badge={service.badge} href="mailto:hello@astrem.co?subject=Photography Inquiry" />
              </ScrollReveal>)}
          </div>
        </div>
      </section>
      
      {/* Fashion Photography Section */}
      <section id="fashion-photography" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luz-model.jpg" alt="Fashion photography session" className="rounded-xl shadow-lg w-full" />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Fashion & Product
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Fashion Photography
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Elevate your brand with professional fashion photography that showcases your products in their best light. We handle everything from model direction to location scouting.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Professional model direction
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Location scouting and studio setups
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Perfect for e-commerce and social media
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Experience with high-end brands and products
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <a href="mailto:hello@astrem.co?subject=Photography Inquiry">Book a Fashion Photography Session</a>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Real Estate Photography Section */}
      <section id="real-estate-photography" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Real Estate
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Real Estate Photography
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Showcase properties in their best light with our professional real estate photography services. We offer both standard MLS photography and premium architectural photography.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Standard MLS photography packages
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Premium architectural photography
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Twilight and special lighting techniques
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Fast turnaround for time-sensitive listings
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/services/real-estate-media">Learn More About Real Estate Media</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luxury-property-photography.jpg" alt="Real estate photography" className="rounded-xl shadow-lg w-full" />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Event Photography Section */}
      <section id="event-photography" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/event_photography_the_real_deal.jpg" alt="Event photography" className="rounded-xl shadow-lg w-full" />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Events
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Event Photography
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Capture the special moments of your corporate events, conferences, and social gatherings with our professional event photography services.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Corporate events and conferences
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Candid and group shots
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Digital gallery delivery
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Shareable links for attendees
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <a href="mailto:hello@astrem.co?subject=Photography Inquiry">Book Event Photography</a>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Virtual Tour Section */}
      <section id="virtual-tours" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Virtual Experience
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Virtual Tour Services
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Take your property marketing to the next level with immersive virtual tours. Our cutting-edge technology creates interactive 3D experiences that allow viewers to explore properties remotely.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">Custom 360 virtual tours</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      360° panoramic photography
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Interactive hotspots and features
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Perfect for real estate, retail, and hospitality
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/services/virtual-tours">Learn More About Virtual Tours</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img alt="Virtual tour technology" className="rounded-xl shadow-lg w-full" src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_setting_up_camera_tripod.jpg" />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Ready to Elevate Your Visual Content?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how our photography services can help showcase your business, products, or properties in their best light.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
              <a href="mailto:hello@astrem.co?subject=Photography Inquiry">Schedule a Photography Consultation</a>
            </Button>
          </ScrollReveal>
        </div>
      </section>
      
      {/* Business Photography Pricing */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Professional Photography Pricing
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              For businesses, brands, and organizations in Baton Rouge and beyond.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {businessPricingCards.map((card, index) => <ScrollReveal key={index} delay={200 + index * 100}>
                <div className="h-full rounded-xl overflow-hidden border border-astrem-gray-200 hover:shadow-lg transition-all">
                  <div className="p-6 md:p-8 bg-white h-full flex flex-col">
                    <h3 className="text-xl font-medium text-astrem-black mb-3">{card.title}</h3>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-semibold text-astrem-black">{card.price}</span>
                    </div>
                    
                    <p className="text-astrem-gray-600 mb-6 flex-grow">{card.description}</p>
                    
                    <Button asChild variant="default" className="w-full rounded-lg bg-primary hover:bg-primary/90">
                      <a href={card.ctaLink}>
                        {card.ctaText}
                      </a>
                    </Button>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>
      
      {/* Personal Photography Pricing */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Personal Photography Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              For families and individuals in the Baton Rouge area.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {personalPricingCards.map((card, index) => <ScrollReveal key={index} delay={200 + index * 100}>
                <div className="h-full rounded-xl overflow-hidden border border-astrem-gray-200 bg-white/80">
                  <div className="p-6 md:p-8 h-full flex flex-col">
                    <h3 className="text-xl font-medium text-astrem-black mb-3">{card.title}</h3>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-semibold text-astrem-black">{card.price}</span>
                    </div>
                    
                    <p className="text-astrem-gray-600 mb-6 flex-grow">{card.description}</p>
                    
                    <Button asChild variant="outline" className="w-full rounded-lg">
                      <a href={card.ctaLink}>
                        {card.ctaText}
                      </a>
                    </Button>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>
      
      <FAQSection faqs={faqs} />
    </PageLayout>
  );
};

export default Photography;
